import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Section } from '../../../../api/requests/MantainerPortal';
import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';

type UseSectionsColumnsProps = {
  onOpenTeachersModal?: (name: string, teachers: string[]) => void;
};

export default function useSectionsColumns({
  onOpenTeachersModal,
}: UseSectionsColumnsProps) {
  const { t } = useTranslation(TranslationsKeys.CONTINUOUS_EDUCATION_SECTIONS);
  const history = useHistory();

  const editSection = useCallback(
    (row: Section) => {
      history.push(`${PathsLayouts.nonStructured}/sections/${row.id}`);
    },
    [history],
  );

  const openTeachersModal = useCallback(
    (row: Section) => {
      if (onOpenTeachersModal) {
        const teacherNames = row.professors.map(
          (prof) => prof.account?.fullName ?? '',
        );
        onOpenTeachersModal(row.name, teacherNames);
      }
    },
    [onOpenTeachersModal],
  );

  const columns = useMemo<ColumnTable<Section>[]>(
    () => [
      {
        columnName: 'name',
        headerText: t(`table.sectionCode`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
      },
      {
        columnName: 'courseCode',
        headerText: t(`table.courseCode`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => {
          return row.course?.code || '';
        },
      },
      {
        columnName: 'courseName',
        headerText: t(`table.courseName`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => {
          return row.course?.name || '';
        },
      },
      {
        columnName: 'schedule',
        headerText: t(`table.schedule`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value?.name || '';
        },
      },
      {
        columnName: 'school',
        headerText: t(`table.school`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => {
          return row.course?.school?.name || '';
        },
      },
      {
        columnName: 'modality',
        headerText: t(`table.modality`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => {
          return row.course?.modality?.name || '-';
        },
      },
      {
        columnName: 'campus',
        headerText: t(`table.campus`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ value }) => {
          return value?.name || '';
        },
      },
      {
        columnName: 'teacher',
        headerText: t(`table.teacher`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => {
          if (!row.professors.length) {
            return '-';
          }

          if (row.professors.length === 1) {
            return row.professors[0]?.account?.fullName ?? '-';
          }

          return (
            <div className="w-full justify-content-center d-flex align-items-center">
              {t(`table.manyTeachers`)}

              <button
                className="btn btn-sm"
                onClick={() => openTeachersModal(row)}
              >
                <Icon name="information" size={16} />
              </button>
            </div>
          );
        },
      },
      {
        columnName: 'id',
        headerText: t(`table.action`),
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => {
          return (
            <div className="d-flex justify-content-center align-items-center">
              <Button
                text={t(`table.edit`)}
                size="sm"
                onClick={() => editSection(row)}
              />
            </div>
          );
        },
      },
    ],
    [t, editSection, openTeachersModal],
  );

  return columns;
}
